import {Customer} from "../types.ts";

// FNP-773: render a human-readable label for a customer instead of a raw UUID.
//
// Two things conspire to show UUIDs in the console today:
//  1. The billing customer `name` is sometimes the account UUID itself — a
//     provisioning fallback bug (FNP-772: `name = email || accountId`).
//  2. Consumers (the Meter Assignments table, the customer dropdown) render the
//     raw `name`/`customerId` with no fallback.
//
// This helper is the single place that decides the display label, so the table
// and the dropdown agree. Precedence: a real (non-UUID) name → the
// server-resolved ownerAccountEmail (FNP-573, meant for exactly this) →
// contactInfo.email → a shortened id. It never returns a full raw UUID as if it
// were a name.

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value?: string | null): boolean =>
    !!value && UUID_RE.test(value.trim());

// A short, support-friendly rendering of an id when nothing better exists
// (e.g. `ac633870…`) — enough to eyeball/copy without dumping the full UUID.
export const shortenId = (id?: string | null): string =>
    id ? `${id.slice(0, 8)}…` : '';

// The label for a customer record. `name` wins only when it's an actual name
// (not the UUID the provisioning bug stamps). Otherwise fall back through the
// email fields, then to a shortened id.
export const customerLabel = (customer?: Customer | null): string => {
    if (!customer) return '';

    const name = customer.name?.trim();
    if (name && !isUuid(name)) return name;

    const email = customer.ownerAccountEmail?.trim() || customer.contactInfo?.email?.trim();
    if (email) return email;

    return shortenId(customer.id);
};

// The label for a bare customer id (the Meter Assignments table only has the
// customerId on each row). Resolve it against a customerId → Customer map, and
// fall back to a shortened id when the customer isn't loaded.
export const customerLabelById = (
    customerId?: string | null,
    customersById?: Map<string, Customer>,
): string => {
    if (!customerId) return '';
    const customer = customersById?.get(customerId);
    return customer ? customerLabel(customer) : shortenId(customerId);
};
